import React, { useEffect, useState, useCallback } from 'react';
import { apiRequest } from '../../utils/api';
import { MessageSetter } from '../../utils/messages';
import { SessionUser } from '../../types';

interface AuditLogsProps {
  setMessage: MessageSetter;
}

interface AuditLog {
  id: string;
  sessionId: string;
  userId: string;
  eventType: string;
  timestamp: string;
  details?: Record<string, unknown>;
}

const AuditLogsSection: React.FC<AuditLogsProps> = ({ setMessage }) => {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [searchString, setSearchString] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedEventType, setSelectedEventType] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [users, setUsers] = useState<SessionUser[]>([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [, setLoadingUsers] = useState(false);

  const eventTypes = ['created', 'started', 'saved', 'resumed', 'completed', 'paused'];

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const data = await apiRequest<SessionUser[]>('/api/users/for-sessions', {}, {
        useAuth: true,
        setMessage,
      });
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  }, [setMessage]);

  const fetchAuditLogs = useCallback(async () => {
    setLoadingAuditLogs(true);
    try {
      const apiUrl = window.location.origin;
      const params = new URLSearchParams();
      if (searchString.trim()) {
        params.append('search', searchString.trim());
      }
      if (selectedUserId) {
        params.append('userId', selectedUserId);
      }
      if (selectedEventType) {
        params.append('eventType', selectedEventType);
      }
      if (startDate) {
        params.append('startDate', startDate);
      }
      if (endDate) {
        params.append('endDate', endDate);
      }
      const queryString = params.toString();
      const url = `${apiUrl}/api/audit-logs${queryString ? `?${queryString}` : ''}`;
      const data = await apiRequest<AuditLog[]>(url, {}, {
        useAuth: true,
        setMessage,
      });
      setAuditLogs(data);
    } finally {
      setLoadingAuditLogs(false);
    }
  }, [searchString, selectedUserId, selectedEventType, startDate, endDate, setMessage]);

  const clearAllFilters = () => {
    setSearchString('');
    setSelectedUserId(null);
    setSelectedEventType(null);
    setStartDate('');
    setEndDate('');
  };

  const hasActiveFilters = searchString.trim() || selectedUserId || selectedEventType || startDate || endDate;

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  return (
    <div className="card">
      <h2 style={{ margin: '0 0 1.5rem 0', fontSize: '1.5rem', color: 'var(--text-primary)' }}>
        Audit Logs
      </h2>
      <p
        style={{
          color: 'var(--text-secondary)',
          marginBottom: '1.5rem',
          fontSize: '0.9rem',
        }}
      >
        View session lifecycle events including when sessions were created, started, saved,
        resumed, and completed.
      </p>

      <div
        style={{
          padding: '1.5rem',
          backgroundColor: 'var(--surface)',
          borderRadius: '8px',
          border: '1px solid var(--grid-line)',
          marginBottom: '1.5rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)' }}>Filters</h3>
          {hasActiveFilters && (
            <button
              className="btn btn-secondary"
              onClick={clearAllFilters}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              Clear All Filters
            </button>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          {/* Search String */}
          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
                fontSize: '0.9rem',
              }}
            >
              Search
            </label>
            <input
              type="text"
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              placeholder="Search session ID, user, or event type..."
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--grid-line)',
                borderRadius: '6px',
                fontSize: '1rem',
                backgroundColor: 'var(--void)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          {/* User Filter */}
          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
                fontSize: '0.9rem',
              }}
            >
              User
            </label>
            <select
              value={selectedUserId || ''}
              onChange={(e) => setSelectedUserId(e.target.value || null)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--grid-line)',
                borderRadius: '6px',
                fontSize: '1rem',
                backgroundColor: 'var(--void)',
                color: 'var(--text-primary)',
              }}
            >
              <option value="">All users</option>
              {users.map((user) => (
                <option key={user.id} value={user.displayName}>
                  {user.displayName}
                </option>
              ))}
            </select>
          </div>

          {/* Event Type Filter */}
          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
                fontSize: '0.9rem',
              }}
            >
              Event Type
            </label>
            <select
              value={selectedEventType || ''}
              onChange={(e) => setSelectedEventType(e.target.value || null)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--grid-line)',
                borderRadius: '6px',
                fontSize: '1rem',
                backgroundColor: 'var(--void)',
                color: 'var(--text-primary)',
              }}
            >
              <option value="">All event types</option>
              {eventTypes.map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
                fontSize: '0.9rem',
              }}
            >
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--grid-line)',
                borderRadius: '6px',
                fontSize: '1rem',
                backgroundColor: 'var(--void)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          {/* End Date */}
          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
                fontSize: '0.9rem',
              }}
            >
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || undefined}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--grid-line)',
                borderRadius: '6px',
                fontSize: '1rem',
                backgroundColor: 'var(--void)',
                color: 'var(--text-primary)',
              }}
            />
          </div>
        </div>

        {hasActiveFilters && (
          <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '4px', fontSize: '0.875rem' }}>
            <strong>Active filters:</strong>{' '}
            {searchString.trim() && <span style={{ marginLeft: '0.5rem' }}>Search: "{searchString}"</span>}
            {selectedUserId && <span style={{ marginLeft: '0.5rem' }}>User: {selectedUserId}</span>}
            {selectedEventType && <span style={{ marginLeft: '0.5rem' }}>Event: {selectedEventType}</span>}
            {startDate && <span style={{ marginLeft: '0.5rem' }}>From: {new Date(startDate).toLocaleDateString()}</span>}
            {endDate && <span style={{ marginLeft: '0.5rem' }}>To: {new Date(endDate).toLocaleDateString()}</span>}
          </div>
        )}
      </div>

      {loadingAuditLogs ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Loading audit logs...</p>
        </div>
      ) : (
        <>
          {auditLogs.length > 0 && (
            <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'rgba(46, 204, 113, 0.1)', borderRadius: '4px', fontSize: '0.875rem' }}>
              <strong>Found {auditLogs.length} audit log{auditLogs.length !== 1 ? 's' : ''}</strong>
              {hasActiveFilters && ' matching the current filters'}
            </div>
          )}
          {auditLogs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
              <p>
                No audit logs found
                {hasActiveFilters ? ' matching the current filters.' : '.'}
              </p>
              {hasActiveFilters && (
                <button
                  className="btn btn-secondary"
                  onClick={clearAllFilters}
                  style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {auditLogs.map((log) => {
            const eventColors: { [key: string]: string } = {
              created: '#3498db',
              started: '#27ae60',
              saved: '#f39c12',
              resumed: '#9b59b6',
              completed: '#2ecc71',
              paused: '#e67e22',
            };

            const eventIcons: { [key: string]: string } = {
              created: '✨',
              started: '▶️',
              saved: '💾',
              resumed: '🔄',
              completed: '✅',
              paused: '⏸️',
            };

            return (
              <div
                key={log.id}
                style={{
                  padding: '1rem',
                  backgroundColor: 'var(--surface)',
                  border: `1px solid ${eventColors[log.eventType] || 'var(--grid-line)'}`,
                  borderRadius: '6px',
                  borderLeft: `4px solid ${eventColors[log.eventType] || '#ddd'}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '1rem',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <span style={{ fontSize: '1.25rem' }}>
                      {eventIcons[log.eventType] || '📝'}
                    </span>
                    <span
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: eventColors[log.eventType] || '#666',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {log.eventType}
                    </span>
                    <span
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--text-muted)',
                        fontFamily: 'monospace',
                        marginLeft: '0.5rem',
                      }}
                    >
                      {log.sessionId}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: '0.875rem',
                      color: 'var(--text-secondary)',
                      marginBottom: '0.25rem',
                    }}
                  >
                    <strong>User:</strong> {log.userId}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    <strong>Time:</strong>{' '}
                    {new Date(log.timestamp).toLocaleString()}
                  </div>
                  {log.details && Object.keys(log.details).length > 0 && (
                    <div
                      style={{
                        marginTop: '0.5rem',
                        padding: '0.5rem',
                        backgroundColor: 'var(--surface)',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {Object.entries(log.details).map(([key, value]) => {
                        if (key === 'totalDuration' || key === 'durationAdded') {
                          return (
                            <div
                              key={key}
                              style={{
                                color: 'var(--cyan-glow)',
                                fontWeight: 600,
                              }}
                            >
                              <strong>{key}:</strong> {String(value)}
                            </div>
                          );
                        }
                        return (
                          <div key={key}>
                            <strong>{key}:</strong> {String(value)}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuditLogsSection;
